/**
 * PRP-MCP-9: the plans sold today (Pro, Pro Plus), the pricing config and the
 * live Founding 20 count. Its own module because `web/src/lib/api.ts` sits at
 * its god-file ceiling (the `chatTrialApi.ts` precedent). Every number comes
 * from the backend (`GET /billing/config`, data in plans.json), never from here.
 */
import { api } from './api';

/** Plan ids from `app/services/billing/data/plans.json`. */
export type PlanId = 'pro' | 'pro_plus';

/** Subscription billing interval. */
export type BillingInterval = 'month' | 'annual';

/** One price point (a Stripe price + its display amount in cents). */
export interface BillingPrice {
  price_id?: string;
  amount_cents: number;
}

/** A plan: the assistant, export, and included conversions per month. */
export interface BillingPlan {
  id: PlanId;
  name: string;
  included_conversions: number;
  /** The included number during the Founding 20 perk. */
  founder_conversions: number;
  monthly: BillingPrice;
  annual: BillingPrice;
}

/** A pay-as-you-go credit pack. */
export interface BillingPack {
  id: string;
  credits: number;
  amount_cents: number;
  label: string;
}

/** Per-conversion credit rules that drive the "How credits work" copy. */
export interface BillingCreditRules {
  free_line_threshold: number;
  per_conversion: number;
  per_conversion_over: number;
  sketch_multiplier: number;
  trial_signup_credits: number;
}

/** Full pricing config returned by GET /billing/config. */
export interface BillingConfig {
  enabled: boolean;
  credits: BillingCreditRules;
  plans: BillingPlan[];
  packs: BillingPack[];
  /** The Founding 20 perk. The live count is `getFounderSeats()`. */
  founder: { total: number; perk_months: number };
  /** The $7 trial; amounts are display only. */
  chat_trial?: {
    available: boolean;
    trial_days: number;
    fee_cents: number;
    base_plan: PlanId;
    included_conversions: number;
  };
}

/**
 * The PRP-MCP-9 fields GET /me/credits adds to `CreditsResponse` (kept here:
 * `web/src/types/index.ts` is at its god-file ceiling). `balance` there is the
 * spendable total, included this month + pack. Absent on older servers.
 */
export interface CreditBalances {
  included_this_month?: number;
  included_per_month?: number;
  pack_credits?: number;
  /** Tier label: pro | pro_plus | importer | member | admin | free. */
  plan?: string;
  founder_until?: string | null;
}

/** GET /api/public/founder-seats. */
export interface FounderSeats {
  remaining: number;
  total: number;
}

const API_BASE: string =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) || 'https://est.anterotrail.com/api/v1';

/** The public seat endpoint lives at the backend origin, outside /api/v1. */
export function founderSeatsUrl(base: string = API_BASE): string {
  return `${base.replace(/\/api\/v1\/?$/, '').replace(/\/$/, '')}/api/public/founder-seats`;
}

export const PlansAPI = {
  getBillingConfig: (): Promise<BillingConfig> =>
    api.get('/billing/config') as Promise<BillingConfig>,

  /** Starts Stripe Checkout for Pro or Pro Plus at the chosen interval. */
  createPlanSession: (plan: PlanId, interval: BillingInterval): Promise<{ url: string }> =>
    api.post('/billing/create-subscription-session', { plan, interval }) as Promise<{ url: string }>,

  /** The live Founding 20 count; no auth. Rejects on any non-200. */
  getFounderSeats: async (): Promise<FounderSeats> => {
    const res = await fetch(founderSeatsUrl());
    if (!res.ok) throw new Error(`founder seats: ${res.status}`);
    return (await res.json()) as FounderSeats;
  },
};
